import React, { useMemo, useCallback /* useState, useEffect */ } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "uikit";
import { Tooltip } from "react-leaflet";
import styled from "styled-components";
import { Popover, Whisper } from "rsuite";

import { Executor /* ExecutorFeedback */ } from "../../../services";
import { useTypedSelector } from "../../../redux/store";
import { useAppContext } from "../../../context";
import { useExecutorStatus } from "../../../hooks";
import { formatNumber } from "../../../utils";

const StyledExecutorCell = styled("div")<{ bgColor: string; fnColor: string }>`
	padding: 6px 10px;
	height: 26px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: ${(props) => props.fnColor || "#fff"};
	font-weight: 700;
	font-size: 11px;
	border-radius: 3px;
	background-color: ${(props) => props.bgColor || "#9BA3A8"};
	border: ${(props) =>
		props.bgColor === "transparent"
			? `1px solid ${theme.colors.color_border_basic};`
			: "none"};
`;

const AliasRow = styled.div<ExecutorInfo.AliasRow>`
	padding: 1px 6px;
	color: ${(props) =>
		props.useTextColor ? props.textColor : theme.colors.primary};
	background-color: ${(props) =>
		props.useBgColor ? props.bgColor : "transparent"};
	height: 100%;
	width: 100%;
	display: flex;
	gap: 5px;
	font-size: 12px;
	white-space: nowrap;
	align-items: center;
	justify-content: flex-start;

	cursor: pointer;

	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;

	${theme.longWord}
`;

const ExecutorInfo: React.FC<ExecutorInfo.Props> = ({
	widgetType,
	id = -1,
	executor: executorProp,
	speed,
	onDoubleClick,
	rating = 0,
}) => {
	const { t } = useTranslation();
	const { getStatus } = useExecutorStatus();
	const { language: lang } = useAppContext();

	const executorFromStore = useTypedSelector((state) => {
		if (executorProp) return undefined;
		return state.globalState.executors.data.subscription.basic?.find(
			(e) => e.id === id,
		);
	});
	const executor = executorProp ?? executorFromStore;
	const speedFromExecutorLocation = useTypedSelector((state) => {
		if (widgetType === "Map" || widgetType === "Executors") {
			return undefined;
		}
		if (speed != null) return undefined;
		return state.globalState.executorLocations.data.subscription.basic?.find(
			(e) => (e.executorId ?? -2) === (executor?.id ?? id),
		)?.speed;
	});

	const speedMain: number = useMemo(
		() =>
			widgetType === "Map"
				? speed || 0
				: speed ?? speedFromExecutorLocation ?? 0,
		[widgetType, speed, speedFromExecutorLocation],
	);

	const labels = useMemo(
		() => ({
			alias: t("orderPageWidgets.parking.parkingTable.str209"),
			fullName: t("orderPageWidgets.parking.parkingTable.str210"),
			status: t("orderPageWidgets.parking.parkingTable.str211"),
			phones: t("orderPageWidgets.parking.parkingTable.str212"),
			carData: t("utils.getTabsModeOne.cars"),
			carType: t("class"),
			online: t("orderPageWidgets.parking.parkingTable.str214"),
			balance: t("balances.main"),
			// order: t("orderPageWidgets.parking.parkingTable.str219"),
			speed: t("orderPageWidgets.parking.parkingTable.str220"),
			priority: t(
				"pages.settings.pages.orders.tabs.ordersDistribution.modal.options.priority",
			),
			// rating: t("utils.getColumnsModeOne.rating"),
			kph: t("units.kph"),
			taxiService: t("taxiService"),
			company: t("company"),
			parkNumber: t("accessText.parkNumber"),
			rating: t("utils.getTabsModeOne.ratingPoints"),
		}),
		[t],
	);

	const executorStatuses = useMemo(
		() => ({
			busy: {
				label: t("orderPageWidgets.parking.parkingTable.str202") ?? "",
				color: "rgb(252, 151, 57)",
				offlineColor: "rgba(252, 151, 57, 0.3)",
				fontColor: theme.colors.primary,
			},
			dinner: {
				label: t("orderPageWidgets.parking.parkingTable.str203") ?? "",
				color: "rgb(33, 150, 243)",
				offlineColor: "rgba(33, 150, 243, 0.3)",
				fontColor: theme.colors.primary,
			},
			home: {
				label: t("orderPageWidgets.parking.parkingTable.str204") ?? "",
				color: "rgb(16, 228, 235)",
				offlineColor: "rgba(16, 228, 235, 0.3)",
				fontColor: theme.colors.primary,
			},
			on_order: {
				label: t("orderPageWidgets.parking.parkingTable.str205") ?? "",
				color: "rgb(255, 128, 130)",
				offlineColor: "rgba(255, 128, 130, 0.3)",
				fontColor: theme.colors.primary,
			},
			available: {
				label: t("orderPageWidgets.parking.parkingTable.str206") ?? "",
				color: "rgb(76, 175, 80)",
				offlineColor: "rgba(76, 175, 80, 0.3)",
				fontColor: theme.colors.primary,
			},
			closed: {
				label: t("orderPageWidgets.parking.parkingTable.str207") ?? "",
				color: "rgb(134, 75, 75)",
				offlineColor: "rgba(134, 75, 75, 0.3)",
				fontColor: theme.colors.white,
			},
			your_order: {
				label: t("orderPageWidgets.parking.parkingTable.str208") ?? "",
				color: "rgb(102, 84, 177)",
				offlineColor: "rgba(102, 84, 177, 0.3)",
				fontColor: theme.colors.white,
			},
		}),
		[t],
	);

	const backgroundColor = useMemo(() => {
		if (!executor?.isWorking) {
			return executor?.online
				? "rgb(190, 190, 190)"
				: "rgba(190, 190, 190, 0.3)";
		}

		const isYourOrderStatus = executor?.executorToOrders?.find(
			(order) => order.source === "executor" && !order.closedAt,
		);

		if (isYourOrderStatus) {
			return executor?.online
				? executorStatuses.your_order.color
				: executorStatuses.your_order.offlineColor;
		}

		return executor?.online
			? executorStatuses[executor.status]?.color
			: executorStatuses[executor.status]?.offlineColor;
	}, [
		executor?.executorToOrders,
		executor?.online,
		executor?.status,
		executor?.isWorking,
		executorStatuses,
	]);

	const fontColor = useMemo(() => {
		if (!executor?.isWorking) {
			return theme.colors.primary;
		}

		const isYourOrderStatus = executor?.executorToOrders?.find(
			(order) => order.source === "executor" && !order.closedAt,
		);

		if (isYourOrderStatus) {
			return executorStatuses.your_order.fontColor;
		}

		return executorStatuses[executor.status]?.fontColor;
	}, [
		executor?.executorToOrders,
		executor?.isWorking,
		executor?.status,
		executorStatuses,
	]);

	const carClassData = useMemo(() => {
		const classData = executor?.executorShiftJournals?.find(
			(j) => j.isLatest,
		)?.car?.class;

		if (!classData) {
			return {
				shortName: "",
				backgroundColor: "",
				textColor: "",
				useBackgroundColor: false,
				useTextColor: false,
			};
		}

		const textColorHex = classData.textColor?.string() ?? "";
		const backgroundColorHex = classData.backgroundColor?.string() ?? "";

		return {
			shortName: classData.shortName ?? "",
			backgroundColor: backgroundColorHex,
			textColor: textColorHex,
			useBackgroundColor: classData.useBackgroundColor ?? false,
			useTextColor: classData.useTextColor ?? false,
		};
	}, [executor?.executorShiftJournals]);

	const getOnlineStatus = useCallback(
		(data: Executor.Model) =>
			data.online
				? t("orderPageWidgets.parking.parkingTable.str200") ?? ""
				: t("orderPageWidgets.parking.parkingTable.str201") ?? "",
		[t],
	);

	const getPersonFullName = useCallback(
		(data: Executor.Model): string =>
			`${data?.person?.lastName ? data.person.lastName : ""} ${
				data?.person?.firstName ? data.person.firstName : ""
			} ${data?.person?.fatherName ? data.person.fatherName : ""}`,
		[],
	);

	const getBalance = useCallback((data: Executor.Model): string => {
		const main =
			data.paymentAccounts?.find((a) => a.type === "main")?.amount ?? 0;

		return formatNumber(main) || "";
	}, []);

	const getPersonPhones = useCallback((data: Executor.Model): string => {
		const phones =
			data.person?.phones?.map((phone) => phone.number)?.join(", ") || "";
		return phones;
	}, []);

	const getCar = useCallback(
		(
			data: Executor.Model,
		): { carData: string; carType: string; parkNumber: string } => {
			const carShiftJournals = data.executorShiftJournals?.length
				? data.executorShiftJournals?.[0]?.car
				: null;

			const executorToCrews = data?.executorToCrews || [];

			const crew = carShiftJournals
				? executorToCrews.find(
						({ crew }) => crew.car.id === carShiftJournals.id,
				  )?.crew
				: data.executorToCrews?.[0]?.crew;

			const car = crew ? crew?.car : data.executorToCrews?.[0]?.crew?.car;

			const registrationNumber =
				car?.additionalFields?.registrationNumber || "";
			const color = car?.color?.name?.[lang] || "";
			const parkNumber = car?.callSign || "";

			return {
				carData: `${
					car?.modelToBodyType?.model?.baseTypeToBrand?.brand?.name ||
					""
				} ${car?.modelToBodyType?.model?.name || ""} ${color || ""} ${
					registrationNumber ? `(${registrationNumber})` : ""
				}`,
				carType: `${car?.class?.name?.[lang] || ""}`,
				parkNumber,
			};
		},
		[lang],
	);

	const getPriority = useCallback(
		(data: Executor.Model): number => data.priority || 0,
		[],
	);

	const executorData = useMemo((): ExecutorInfo.ExecutorData | null => {
		if (!executor) return null;

		const car = getCar(executor);
		const time = speedMain || 0;
		const modifySpeed = Number(time * 3.6).toFixed(1);

		const taxiService = executor?.taxiService?.settlement?.[lang] || "";
		const company = executor?.taxiService?.company?.name?.[lang] || "";

		const payload = {
			alias: executor?.alias ?? "",
			fullName: getPersonFullName(executor) ?? "",

			status: getStatus(executor) ?? "",
			phones: getPersonPhones(executor) ?? "",

			carData: car.carData ?? "",
			carType: car.carType ?? "",
			parkNumber: car.parkNumber ?? "",
			online: getOnlineStatus(executor) ?? "",

			balance: getBalance(executor) ?? "",

			// order: getLastOrder(executor) ?? "",
			speed: `${modifySpeed} ${labels.kph}`,
			priority: getPriority(executor) ?? "",
			company,
			taxiService,
		};

		return payload;
	}, [
		executor,
		getCar,
		speedMain,
		lang,
		getPersonFullName,
		getStatus,
		getPersonPhones,
		getOnlineStatus,
		getBalance,
		labels.kph,
		getPriority,
	]);

	if (!executor || !executorData) return null;

	const InfoComponent = (
		<div>
			<div>
				{labels.alias}: {executorData.alias}
			</div>
			<div>
				{labels.status}: {executorData.status}
			</div>
			<div>
				{labels.online}: {executorData.online}
			</div>
			<div>
				{labels.fullName}: {executorData.fullName}
			</div>
			<div>
				{labels.phones}: {executorData.phones}
			</div>

			<div>
				{labels.parkNumber}: {executorData.parkNumber}
			</div>
			<div>
				{labels.carData}: {executorData.carData}
			</div>
			<div>
				{labels.carType}: {executorData.carType}
			</div>
			{/* <div>
				{labels.order}: {executorData.order}
				</div> */}
			<div>
				{labels.speed}: {executorData.speed}
			</div>
			<div>
				{labels.balance}: {executorData.balance}
			</div>
			<div>
				{labels.priority}: {executorData.priority}
			</div>
			<div>
				{labels.rating}: {rating}
			</div>
			<div>
				{labels.company}: {executorData.company}
			</div>
			<div>
				{labels.taxiService}: {executorData.taxiService}
			</div>

			{/* <div>
				{labels.rating}: {rating}
			</div> */}
		</div>
	);

	return (
		<>
			{widgetType === "Executors" && (
				<Whisper
					speaker={<Popover>{InfoComponent}</Popover>}
					placement="bottom"
				>
					<AliasRow
						bgColor={carClassData.backgroundColor}
						textColor={carClassData.textColor}
						useBgColor={carClassData.useBackgroundColor}
						useTextColor={carClassData.useTextColor}
						onDoubleClick={() => {
							if (onDoubleClick) onDoubleClick();
						}}
						style={{ justifyContent: "center" }}
					>
						{`${executorData.alias} ${
							carClassData.shortName || ""
						}`}
					</AliasRow>
				</Whisper>
			)}
			{widgetType === "Map" && (
				<Tooltip direction="bottom">{InfoComponent}</Tooltip>
			)}
			{widgetType === "Parking" && (
				<Whisper
					speaker={<Popover>{InfoComponent}</Popover>}
					placement="bottom"
				>
					<StyledExecutorCell
						bgColor={backgroundColor}
						fnColor={fontColor}
					>
						{executorData.alias}
					</StyledExecutorCell>
				</Whisper>
			)}
		</>
	);
};

declare namespace ExecutorInfo {
	interface Props {
		widgetType: "Parking" | "Executors" | "Map";
		id?: number;
		executor?: Executor.Model;
		speed?: number;
		onDoubleClick?: () => void;
		rating?: number;
	}

	interface ExecutorData {
		alias: string;
		online: string | number;
		status: string | number;
		priority: string | number;
		speed: string;
		carType: string;
		carData: string;
		phones: string;
		balance: string;
		fullName: string;
		taxiService?: string;
		company?: string;
		parkNumber?: string;

		// order: string;
	}

	interface AliasRow {
		bgColor: string;
		textColor: string;
		useBgColor: boolean;
		useTextColor: boolean;
	}
}

export default React.memo(ExecutorInfo);
